/**
 * Shared blog card matching the DGTG "Latest insights" design language.
 * Used across every site (Auto, Finance, Coupons, DGTG, DevMob, Games, Travel, Tech).
 *
 * Props
 *  - article  : { id, slug, title, excerpt, featured_image, featured_image_alt, author, updated_at, site }
 *  - to       : Link destination (usually `${basePath}/${slug || id}`)
 *  - accent   : brand accent color used for hover title + border glow (e.g. '#EA580C' for auto)
 *  - fallbackImage : image path used when featured_image is empty or 404s
 *  - site     : site id override (falls back to article.site) — used to link author to the correct /:site/author/:slug
 */
import { Link } from 'react-router-dom';
import { useState } from 'react';
import AuthorByline from './AuthorByline';
import SponsoredBadge from './SponsoredBadge';
import { sized, sizedSrcSet } from '@/lib/imageSize';

// Per-site default OG cards live in /public — perfect as a last-resort fallback
// so a broken image never leaves an ugly empty grey box.
const SITE_DEFAULT_IMAGE = {
  auto: '/og-cars.jpg',
  coupon: '/og-metromandi.jpg',
  finance: '/og-finance.jpg',
  dgtg: '/og-dgtg.jpg',
  games: '/og-games.jpg',
  vikasifications: '/og-vikas.jpg',
  vikas: '/og-vikas.jpg',
  devmob: '/og-devmob.jpg',
  travel: '/og-woketraveler.jpg',
  tech: '/og-tech-n-ai-talks.jpg',
};

export default function BlogCard({ article: a, to, accent = '#111827', fallbackImage = '', site: siteOverride }) {
  const site = siteOverride || a.site;
  const [broken, setBroken] = useState(false);
  const finalFallback = fallbackImage || SITE_DEFAULT_IMAGE[site] || '/og-default.jpg';
  const img = (!broken && a.featured_image) ? a.featured_image : finalFallback;

  return (
    <Link
      to={to}
      className="group border border-gray-200 rounded-2xl overflow-hidden hover:shadow-lg transition-all bg-white block"
      onMouseEnter={(e) => { e.currentTarget.style.borderColor = `${accent}30`; }}
      onMouseLeave={(e) => { e.currentTarget.style.borderColor = ''; }}
      data-testid={`blog-card-${a.id}`}
    >
      <div className="aspect-video bg-gray-100 overflow-hidden">
        <img
          src={sized(img, 640)}
          srcSet={broken ? undefined : sizedSrcSet(img)}
          sizes="(max-width: 640px) 100vw, 400px"
          alt={a.featured_image_alt || a.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          loading="lazy"
          onError={(e) => {
            if (!broken) {
              setBroken(true);
              // Give the browser one nudge to swap to the fallback image now
              e.currentTarget.src = finalFallback;
            } else {
              // If fallback also fails, drop the onerror so we don't loop.
              e.currentTarget.onerror = null;
            }
          }}
        />
      </div>
      <div className="p-6">
        {a.is_sponsored && <SponsoredBadge article={a} className="mb-2" />}
        <h2
          className="font-bold text-gray-900 mb-2 transition-colors line-clamp-2"
          onMouseEnter={(e) => { e.currentTarget.style.color = accent; }}
          onMouseLeave={(e) => { e.currentTarget.style.color = ''; }}
        >
          {a.title}
        </h2>
        {a.excerpt && <p className="text-sm text-gray-500 mb-3 line-clamp-2">{a.excerpt}</p>}
        <AuthorByline
          articleAuthor={a.author}
          site={site}
          date={a.updated_at}
          accent={accent}
          size="sm"
          asLink={false}
        />
      </div>
    </Link>
  );
}
